import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Ticket, CheckCircle2, Users } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ invite_code: "", company_name: "", name: "", email: "", password: "" });
  const [invite, setInvite] = useState(null); // {valid,type,company_name,reason}
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const inspectCode = async () => {
    const code = form.invite_code.trim();
    if (!code) { setInvite(null); return; }
    setChecking(true);
    try {
      const { data } = await api.get(`/invite/${encodeURIComponent(code)}`);
      setInvite(data);
    } catch { setInvite({ valid: false, reason: "invalid" }); }
    finally { setChecking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const isTeam = invite?.valid && invite.type === "team";
  const isWorkspace = invite?.valid && invite.type === "workspace";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1733665921380-27c7d02f3b0b" alt="Construction site"
             className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#040316]/75" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-display font-black text-4xl tracking-tight leading-tight">Your procurement<br/>command center.</h2>
          <p className="mt-3 text-white/80 max-w-md">Set up your company workspace in seconds and start routing bids.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <p className="font-display font-black tracking-tight text-xl">Bid Router</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground -mt-0.5">by Conduit Systems</p>
            </div>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight">Create account</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Sign up is invite-only. Enter the code you received.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest flex items-center gap-1"><Ticket className="h-3.5 w-3.5" />Invite code</Label>
              <Input value={form.invite_code} onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })} onBlur={inspectCode} required
                     data-testid="register-invite" className="mt-1.5 font-mono uppercase" placeholder="BR-____-____" />
              {checking && <p className="text-xs text-muted-foreground mt-1">Checking code…</p>}
              {invite && !invite.valid && <p className="text-xs text-destructive mt-1" data-testid="invite-invalid">Invalid or expired invite code.</p>}
              {isTeam && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="invite-team">
                  <Users className="h-3.5 w-3.5" />Joining <span className="font-medium">{invite.company_name}</span>'s workspace
                </p>
              )}
              {isWorkspace && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="invite-workspace">
                  <CheckCircle2 className="h-3.5 w-3.5" />Valid — you'll create a new company workspace
                </p>
              )}
            </div>
            {!isTeam && (
              <div>
                <Label className="text-xs uppercase tracking-widest">Company name</Label>
                <Input value={form.company_name} onChange={set("company_name")} required={!isTeam}
                       data-testid="register-company" className="mt-1.5" placeholder="Acme Construction" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Your name</Label>
              <Input value={form.name} onChange={set("name")} required
                     data-testid="register-name" className="mt-1.5" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} required
                     data-testid="register-email" className="mt-1.5" placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Password</Label>
              <Input type="password" value={form.password} onChange={set("password")} required
                     data-testid="register-password" className="mt-1.5" placeholder="••••••••" />
              <p className="text-xs text-muted-foreground mt-1">At least 12 characters, with an uppercase letter, a lowercase letter, and a number.</p>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
              {loading ? "Creating…" : (isTeam ? "Join workspace" : "Create workspace")}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6">
            Already have an account? <Link to="/login" className="text-primary font-medium" data-testid="go-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
